import { ChangeEvent, FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Option } from 'types/options';
import ListCollection from 'types/redux/ListCollection';
import { Routes } from 'types/routes';

import Course from 'models/Course';
import CourseCategory from 'models/CourseCategory';

import { parseToQueryString } from 'utils/querystring';

import { list as listCourses } from 'requests/courses';

import SectionCourseTiles from 'components/content/sections/CourseTiles';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import Select from 'components/layout/forms/Select';
import { Sizes } from 'components/layout/LayoutContainer';
import LayoutContainer from 'components/layout/LayoutContainer/component';
import Pagination from 'components/layout/Pagination';
import Spinner from 'components/layout/Spinner/component';
import useIsFirstRender from 'hooks/useIsFirstRender';
import useJoinCourseSchedule from 'hooks/useJoinCourseSchedule';
import useLikeCourse from 'hooks/useLikeCourse';

import StyledComponent from './styles';
import { Props } from './types';

interface HandleValue {
    [key: string]: string | number | Option<any>;
}

interface FormState {
    search: string;
    category: Option<CourseCategory>;
    categoryId?: string;
}

const SectionCourses: FunctionComponent<Props> = ({ translations, courses, courseCategories, initialLeadId }) => {
    const { likeCourse } = useLikeCourse();
    const { joinCourseSchedule } = useJoinCourseSchedule();
    const leadIdRef = useRef<string>(initialLeadId);
    const [formValues, setFormValues]: [FormState, Function] = useState(null);
    const [collection, setCollection]: [ListCollection<Course>, Function] = useState(courses);

    const router = useRouter();
    const isFirstRender = useIsFirstRender();

    const isNotEmpty = collection?.elements?.length > 0;
    const isEmpty = collection?.elements?.length === 0;

    const displayCategory = collection?.elements?.[0]?.category?.id === formValues?.category?.value?.id;
    const categoriesOptions = courseCategories?.elements?.map(category => ({ label: category?.name, value: category }));

    const onLike = (course: Course) => {
        likeCourse(course);
    };

    const onJoin = (course: Course) => {
        joinCourseSchedule(course);
    };

    const onMoreCourses = (): Promise<Boolean> => router.push(Routes.PublicCourses);

    const onSubmit = (event: FormEvent<HTMLFormElement> = null, page: number = 1) => {
        event?.preventDefault();

        setCollection((prevState: ListCollection<Course>) => ({
            ...prevState,
            isLoaded: false,
            isLoading: true,
        }));

        listCourses({
            page,
            perPage: 6,
            search: formValues?.search,
            leadId: leadIdRef.current,
            categoryId: formValues?.category?.value?.id,
        })
            .then((response) => {
                setCollection((prevState: ListCollection<Course>) => ({
                    ...prevState,
                    ...response?.courses,
                    elements: response?.courses?.elements,
                }));

                if (response?.courses?.elements?.length > 0) {
                    router.push(Routes.PublicCourses, { hash: 'results' }, { shallow: true });
                }
            });
    };

    const handleChange = (value: HandleValue) => setFormValues((prevState: FormState) => ({ ...prevState, ...value }));

    useEffect(() => {
        let timeOut: NodeJS.Timeout;

        if (!isFirstRender) timeOut = setTimeout(() => {
            onSubmit(null);
        }, 500);

        return () => clearTimeout(timeOut);
    }, [formValues]);

    useEffect(() => {
        if (router.query?.search || router.query?.categoryId) {
            let newFormValues = {
                ...formValues,
                ...router.query,
            };

            if (router.query?.categoryId) {
                const tmp = categoriesOptions.find(elem => elem.value.id === router.query?.categoryId);
                newFormValues = { ...newFormValues, category: tmp };
            }

            setFormValues({
                ...newFormValues,
            });
        }
    }, []);

    return (
        <StyledComponent className="section-courses">
            <LayoutContainer size={Sizes.Medium}>
                <Form
                    className="courses-serach-form"
                    onSubmit={onSubmit}
                >
                    <Input
                        name="search"
                        type="text"
                        className="input-search"
                        placeholder={translations?.inputSearch}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange({ search: e.target.value })}
                        value={formValues?.search}
                    />
                    <Select
                        name="category"
                        onChange={(value: Option<Course>) => {
                            handleChange({ category: value });
                            router.replace({
                                pathname: Routes.PublicCourses,
                                query: value?.value?.id ? { categoryId: value?.value?.id } : null,
                            }, undefined, { shallow: true });
                        }}
                        placeholder={translations?.selectCategories}
                        options={categoriesOptions}
                        value={formValues?.category}
                    />
                </Form>
                <div
                    className="courses-results"
                    id="results"
                >
                    {collection?.isLoading && (
                        <Spinner />
                    )}
                    {(collection?.isLoaded && isNotEmpty) && (
                        <>
                            <SectionCourseTiles
                                courses={collection}
                                translations={{
                                    headline: displayCategory && formValues?.category?.label,
                                    courseCtaButton: translations?.courseCtaButton,
                                    alreadyScheduledButton: translations?.alreadyScheduledButton,
                                }}
                                onJoin={onJoin}
                                onLike={onLike}
                                onMoreCourses={onMoreCourses}
                            />
                            <Pagination
                                currentPage={collection?.meta?.currentPage}
                                lastPage={collection?.meta?.lastPage}
                                hrefBuilder={(currentPage) => parseToQueryString(Routes.PublicCourses, { page: currentPage })}
                                onPageChange={(nextPage: number) => {
                                    if (nextPage === collection?.meta?.currentPage) return;

                                    onSubmit(undefined, nextPage);
                                }}
                            />
                        </>
                    )}
                    {(collection?.isLoaded && isEmpty) && (
                        <div className="courses-results-empty">
                            <p className="empty-title">
                                {translations?.emptyResultsInfo}
                            </p>
                            <p className="empty-description">
                                {translations?.emptyResultsTextOne}
                                <span
                                    className="empty-description-click"
                                    onClick={() => {
                                        setFormValues({
                                            search: null,
                                            category: null,
                                        });

                                        if (router?.query?.leadId) {
                                            leadIdRef.current = undefined;
                                            router.replace(Routes.PublicCourses, undefined, { shallow: true });
                                        }

                                        onSubmit();
                                    }}
                                >
                                    {translations?.emptyResultsButton}
                                </span>
                                {translations?.emptyResultsTextTwo}
                            </p>
                        </div>
                    )}
                </div>
            </LayoutContainer>
        </StyledComponent>
    );
};

export default SectionCourses;
